/**
 * history.ts — scroll-left history paging, as PURE FUNCTIONS.
 *
 * WHY THIS FILE EXISTS
 * --------------------
 * Andrei, 2026-08-25: "создай карточку на возможность догрузки данных для графика из кеша. Сейчас
 * график жестко обрезан слева. Я хотел бы догружать данные по мере скролинга влево если мне это
 * необходимо." The chart IS cut off dead on the left: `/api/bars` serves the newest N bars and
 * `BARS_LIMIT_DEFAULT` is 600, which on 15m is 6.25 days. The data is already in the cache, so
 * `GET /api/bars/{asset}/{tf}/history?before=<time>` hands back the page to the left of it.
 *
 * Everything that can be got WRONG here is a pure function in this file, for the reason gate 5
 * exists at all: the 2026-07-27 incident was frontend state that pytest structurally could not
 * reach, and a splice living inside a `subscribeVisibleLogicalRangeChange` callback in `main.ts` is
 * exactly as unreachable. The four decisions are:
 *
 *   1. WHETHER to ask for another page (`shouldLoadOlder`)
 *   2. WHAT the joined series is (`mergeOlder`)
 *   3. WHERE the viewport must sit afterwards so the chart does not jump (`shiftedRange`)
 *   4. WHEN to stop asking (`advanceHistory` / `HistoryState.exhausted`)
 *
 * THE FORMING BAR IS SAFE BY CONSTRUCTION, NOT BY A CHECK, and that is the card's stated main risk.
 * `mergeOlder` only ever prepends rows STRICTLY OLDER than the series' current first bar and
 * returns the existing array's tail untouched, so the last element — the live candle `/api/live`
 * keeps writing through `updateBar` — cannot be replaced, reordered or duplicated by a history
 * page. The server half is structural too: the cache stores CLOSED bars only, so a forming bar is
 * not in the store `/history` reads. Two independent reasons, neither of which is a comparison
 * against a wall clock.
 */

/** One OHLC row, exactly `chartpane.Bar`. Duplicated as a local structural type rather than
 *  imported so this module stays free of the chart library — these functions are arithmetic on
 *  times, and a dependency on `chartpane` would drag `lightweight-charts` into their tests. */
export type HistoryBar = { time: number; open: number; high: number; low: number; close: number };

/**
 * How many history pages ONE PANE may splice, ever.
 *
 * DERIVED FROM THE DENSITY ARGUMENT THIS APP ALREADY MADE, not chosen. The 2026-07-31 row cap
 * rejected 0.15 px/bar as ~92% sub-pixel waste; measured on BTC/15m in a 760px pane
 * (`process/reviews/t_dc8abf16/measurement.txt`), the initial 960-bar load plus SIX 600-bar pages
 * is 4,560 bars = 47.5 days = 0.17 px/bar and 366 KB cumulative, while the SEVENTH page lands
 * exactly on 0.15 — the density already judged not worth paying for. So six is the last page that
 * stays above the line, and the ceiling has a reason rather than a round number.
 *
 * THE SERVER CANNOT ENFORCE THIS. `/api/bars/{asset}/{tf}/history` is stateless and is told nothing
 * but a cursor, so it cannot know how many pages this pane already holds; it caps ONE PAGE
 * (`main.HISTORY_PAGE_BARS`) and the count of pages is capped here. Both halves are needed.
 *
 * ON MOST TIMEFRAMES THIS NEVER BITES: BTC/1D exhausts the cache at page 3 (2,198 bars), so the
 * honest end of the data is reached before the ceiling is. It exists for 15m and 5m, where the
 * cache is deep enough to keep answering.
 */
export const MAX_HISTORY_PAGES = 6;

/**
 * How close to the left edge, in BARS, a scroll must come before the next page is requested.
 *
 * A LEAD, not a trigger at zero: the fetch takes a round trip (8 ms server side, plus Andrei's
 * transatlantic VPN — see `livepoll.ts`, which measured ~+300 ms per call through Paris), so asking
 * only once the leftmost bar is on screen guarantees he sees the cut edge before the data arrives.
 * Fifty bars is under 8% of a page and about 6% of the 15m pane's opening window, so the request
 * lands while the edge is still off screen at any realistic drag speed.
 */
export const HISTORY_LEAD_BARS = 50;

/** Per-pane paging state. One record per pane, because each pane has its own series and its own
 *  left edge — the same reason `markers` and `drawState` are per pane in `ChartPane`. */
export type HistoryState = {
  /** How many pages this pane has spliced. Compared against `MAX_HISTORY_PAGES`. */
  pages: number;
  /** A request is in flight. Without this a single drag fires a page per animation frame. */
  loading: boolean;
  /**
   * There is nothing more to load — either the cache's left edge was reached (the server answered
   * `has_more: false`) or `MAX_HISTORY_PAGES` was hit.
   *
   * A STATE, NOT AN ERROR, which is the card's explicit requirement: the cache is finite (~52 days
   * of 15m measured today) and running out of history is the normal answer to a long scroll. The UI
   * says so once and then stops asking, rather than spinning empty requests or showing a failure.
   */
  exhausted: boolean;
  /** Why it is exhausted, for the disclosure line. `null` while more is available. */
  reason: 'cache-edge' | 'page-ceiling' | null;
  /**
   * The logical left edge this pane was last seen at, or `null` before its first range event.
   *
   * Half of the "did the user scroll LEFT" question — the other half is `armed` below. `null` on the
   * first event is load-bearing: there is no earlier position to have moved left from.
   */
  lastFrom: number | null;
  /**
   * Has the USER touched this pane since its series was loaded?
   *
   * THIS IS THE ONLY THING THAT DISTINGUISHES A SCROLL FROM THE APP MOVING THE CHART, and it took
   * three measured attempts to establish that. `process/reviews/t_dc8abf16/probe_boot_range_events.mjs`
   * printed the real boot sequence for BTC/15m, twice:
   *
   *     +437ms  bars=960   lastFrom=881     <- a range event before the layout write
   *     +460ms  bars=960   lastFrom=0       <- `resetChartLayout`'s fit: an 881-bar LEFTWARD move
   *     +497ms  bars=1560  pages=1          <- a page loaded, with no gesture at all
   *
   * So (a) an edge-PROXIMITY rule fires on boot, because `fitContent` puts `from` at the oldest bar;
   * (b) a "moved LEFT since the last event" rule fires too, because that fit IS a leftward move of
   * 881 bars; and (c) a flag set right after `resetChartLayout` does not help either — the second
   * probe run showed the page still loading, because lightweight-charts delivers range events
   * ASYNCHRONOUSLY, so the app's own fit is reported AFTER the load path has finished running.
   *
   * From the library's point of view an app-driven fit and a user drag are the same event. The
   * difference is not in the event, it is in whether a human did anything — which the app can only
   * learn from an INPUT event on the pane's canvas. So the pane arms on `wheel`/`pointerdown` (see
   * `main.ts`'s pane wiring) and `resetHistory` disarms it on every load.
   *
   * The cost of getting this wrong is not cosmetic: every single page load fetched an extra 48 KB
   * page nobody asked for, in an app whose data volume Andrei has objected to twice.
   */
  armed: boolean;
};

export function initialHistoryState(): HistoryState {
  return { pages: 0, loading: false, exhausted: false, reason: null, lastFrom: null, armed: false };
}

/**
 * Record that the user has interacted with this pane, so its range changes may now be read as
 * scrolling.
 *
 * `lastFrom` is deliberately KEPT: wherever the viewport currently sits is exactly what the gesture
 * about to happen must be measured against, and clearing it would make the first scroll look like a
 * first event and be swallowed.
 */
export function armHistory(state: HistoryState): HistoryState {
  return state.armed ? state : { ...state, armed: true };
}

/**
 * Should this pane ask for another page right now?
 *
 * THREE CONDITIONS, and each closed a hole this feature was measured falling into:
 *
 *   1. THE USER HAS TOUCHED THIS PANE since its load (`state.armed`). See `HistoryState.armed` for
 *      the boot trace: without it, the app's own `fitContent` loads a page on every single load.
 *   2. THE VIEWPORT MOVED LEFT since the last event: `range.from` is below `state.lastFrom`. A pane's
 *      FIRST event has no previous position and therefore cannot be a scroll.
 *   3. IT IS WITHIN THE LEAD OF THE LEFT EDGE. `range.from` is lightweight-charts' LOGICAL left edge:
 *      an index into the series that goes NEGATIVE once the user scrolls past the oldest bar, so
 *      "within `HISTORY_LEAD_BARS` of the edge" is `from < HISTORY_LEAD_BARS` and a scroll past the
 *      edge satisfies it too.
 *
 * REFUSES on: no range (the chart has no viewport yet), a request already in flight, an exhausted
 * pane, and an EMPTY series. The last is not defensive noise — `before` must be a real bar time, and
 * a pane mid-load has no first bar to take a cursor from, which is precisely when the range callback
 * fires (`setBars` triggers it).
 */
export function shouldLoadOlder(
  range: { from: number; to: number } | null,
  state: HistoryState,
  barCount: number,
): boolean {
  if (range === null) return false;
  if (!state.armed) return false;
  if (state.loading || state.exhausted) return false;
  if (barCount === 0) return false;
  if (state.lastFrom === null || range.from >= state.lastFrom) return false;
  return range.from < HISTORY_LEAD_BARS;
}

/**
 * Record where the viewport's left edge now is.
 *
 * SEPARATE FROM `shouldLoadOlder` because it must happen on EVERY range event, including the ones
 * that do not trigger a page: the position is how the next event knows which way the user moved.
 * Folding it into the predicate would make a pure question mutate state, and skipping it on a
 * non-triggering event would make one scroll left look like two.
 *
 * A NO-OP WHEN THE POSITION HAS NOT MOVED, so the state object's identity is stable and a caller can
 * skip a re-render.
 */
export function noteRange(
  state: HistoryState,
  range: { from: number; to: number } | null,
): HistoryState {
  if (range === null) return state;
  if (state.lastFrom === range.from) return state;
  return { ...state, lastFrom: range.from };
}

/**
 * The joined series: `older` prepended to `existing`, strictly ascending, no duplicates.
 *
 * THE JOIN IS THE DANGEROUS PART and the card names it: "дописывание в начало обязано сохранять
 * строгую возрастающую сортировку по времени и не создавать дубль на стыке." Three rules, all
 * enforced here rather than trusted:
 *
 *   * ANY row at or after `existing[0].time` IS DROPPED. The server already returns strictly older
 *     rows (`bars_cache_backend.read_before` excludes the cursor arithmetically), so this is the
 *     second of two independent guards — and it is the one that also covers a page overlapping by
 *     more than the boundary bar, which a client-side cursor bug could produce.
 *   * THE PAGE IS SORTED AND DEDUPED among itself, so a backend that ever answered out of order
 *     cannot put the chart's series out of order. lightweight-charts renders an unsorted series
 *     confidently and wrongly rather than refusing, which is the failure mode this repo exists to
 *     refuse.
 *   * `existing` IS RETURNED UNTOUCHED as the tail. Its last element is the FORMING bar whenever
 *     the source is live, so history paging cannot overwrite the live candle — the card's primary
 *     risk, closed structurally.
 *
 * Returns the SAME array reference when nothing is added, so a caller can skip a `setData` — a
 * pointless redraw would discard the viewport the user is dragging.
 */
export function mergeOlder(
  existing: readonly HistoryBar[],
  older: readonly HistoryBar[],
): HistoryBar[] {
  if (existing.length === 0) return dedupeAscending(older);
  const cut = existing[0].time;
  const keep = dedupeAscending(older.filter((b) => b.time < cut));
  if (keep.length === 0) return existing as HistoryBar[];
  return [...keep, ...existing];
}

/** Ascending, one row per timestamp, LAST occurrence winning.
 *
 *  Last rather than first because a repeated timestamp within one page can only come from the
 *  backend, and the newest copy is the one the store most recently wrote — the same rule
 *  `hl_source.refresh_live_bar` applies when it merges a bar that closed mid-poll. */
function dedupeAscending(rows: readonly HistoryBar[]): HistoryBar[] {
  const byTime = new Map<number, HistoryBar>();
  for (const r of rows) byTime.set(r.time, r);
  return [...byTime.values()].sort((a, b) => a.time - b.time);
}

/**
 * Where the viewport must be re-aimed after `added` bars were prepended.
 *
 * WHY THIS IS NECESSARY AT ALL. `setData` replaces the series and lightweight-charts keeps the
 * visible LOGICAL range — indices into the array. Prepending 600 bars renumbers every existing bar
 * by +600, so the untouched logical range now points 600 bars further back: the chart JUMPS
 * backwards by a whole page the instant the page lands, and the bars the user was looking at fly
 * off to the right. Shifting the range by exactly the number of rows added leaves the same CANDLES
 * under the same pixels.
 *
 * `null` in, `null` out: with no viewport there is nothing to preserve.
 */
export function shiftedRange(
  range: { from: number; to: number } | null,
  added: number,
): { from: number; to: number } | null {
  if (range === null) return null;
  if (added <= 0) return range;
  return { from: range.from + added, to: range.to + added };
}

/**
 * The state after a page came back.
 *
 * TWO INDEPENDENT REASONS TO STOP, reported separately because they mean different things to the
 * reader: `cache-edge` says the data does not exist (the honest end of ~52 days of 15m), while
 * `page-ceiling` says this app declined to hold more in the browser. Collapsing them into one
 * boolean would tell a user "no more history" when the truth is "no more than we will draw".
 *
 * THE CACHE EDGE WINS when both apply, because it is a fact about the world and the ceiling is a
 * policy: telling someone we capped them when there was nothing left anyway is a false claim about
 * the cache.
 *
 * `added` is what `mergeOlder` actually spliced, not what the server sent. A page whose every row
 * was dropped at the join must not be counted as progress, or the ceiling would be spent on
 * nothing — and a pane could sit "loading" forever against a cursor that never advances.
 */
export function advanceHistory(
  state: HistoryState,
  opts: { added: number; serverHasMore: boolean },
): HistoryState {
  const pages = state.pages + (opts.added > 0 ? 1 : 0);
  // `lastFrom` AND `armed` ARE CARRIED THROUGH every branch. `lastFrom` describes where the VIEWPORT
  // is, which a page landing does not change (the caller re-aims the range by exactly the bars added,
  // so the same candles stay under the same pixels) — reset here, the next range event would look
  // like a FIRST event and the user's next scroll left would be swallowed. `armed` describes whether
  // this pane's own load is finished, which a page does not undo.
  const { lastFrom, armed } = state;
  if (!opts.serverHasMore || opts.added === 0) {
    return { pages, loading: false, exhausted: true, reason: 'cache-edge', lastFrom, armed };
  }
  if (pages >= MAX_HISTORY_PAGES) {
    return { pages, loading: false, exhausted: true, reason: 'page-ceiling', lastFrom, armed };
  }
  return { pages, loading: false, exhausted: false, reason: null, lastFrom, armed };
}

/**
 * The disclosure line for a pane's paging state, or `null` for "say nothing".
 *
 * NOTHING IS SAID IN THE ORDINARY CASE, which is the standard this app settled on when the weekly
 * profile's permanent grey furniture was deleted (`.hermes.md` §11b: "a row that is always there is
 * read as chrome and stops being information"). A pane with more history available prints no row.
 *
 * THE TWO STOP REASONS ARE NAMED IN WORDS, never as a bare "no more data": "the cache ends here" is
 * a statement about the store, "not loading more" is a statement about this app's own limit, and a
 * reader deciding whether to trust the left edge of their chart needs to know which one they hit.
 */
export function historyNote(state: HistoryState): { text: string; title: string } | null {
  if (state.loading) {
    return {
      text: 'loading history…',
      title: 'Reading the next page of older bars from the cache. No venue request is made — '
        + 'history paging reads the bar cache only.',
    };
  }
  if (!state.exhausted) return null;
  if (state.reason === 'page-ceiling') {
    return {
      text: `history: ${state.pages} pages loaded (limit)`,
      title: `This pane holds ${state.pages} pages of extra history, this app's ceiling of `
        + `${MAX_HISTORY_PAGES}. Older bars may exist in the cache; they are not loaded because at `
        + 'this density the bars are already below one pixel each (measured 0.17 px/bar in a 760px '
        + 'pane). Reload the pane to start over.',
    };
  }
  return {
    text: 'history: cache ends here',
    title: 'The bar cache holds nothing older than the leftmost candle. This is the end of the '
      + 'stored data, not an error — history paging deliberately never asks the venue.',
  };
}
